import React, { useEffect, useState } from 'react'
import {
  type Organization,
  OrganizationsHeader,
  OrganizationsTable,
  type OrgFilters,
  OrganizationsFilters,
  type MissingValueFilter
} from 'components'
import { Box, CircularProgress, Fab, Grid, Pagination, Typography } from '@mui/material'
import { useSessionContext } from 'session'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate } from 'react-router-dom'
import { type OrgFilterResults, orgListFilter } from 'services'
export const OrganizationPage: React.FC = () => {
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const navigate = useNavigate()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const initialState: OrgFilters = {
    id: undefined,
    uniqueId: '',
    name: '',
    type: 0,
    missing_value: undefined,
    country: null,
    country_name: null,
    parent_orgs: [],
    page: 1,
    order_field: 'id',
    order_direction: 'ASC'
  }
  const [filters, setFilters] = useState<OrgFilters>(initialState)
  const [lastQueriedFilters, setLastQueriedFilters] = useState<OrgFilters>(initialState)
  const [loadResultWithFilters, setLoadResultWithFilters] = useState(false)
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    changeModuleName('Organizations')
    changeUniqueId('')
  }, [changeModuleName])

  const updateFilters = (filters: any): void => {
    setFilters(prevState => ({ ...prevState, ...filters }))
  }

  const cleanFilter = (): void => {
    const diff = JSON.stringify(initialState) === JSON.stringify(lastQueriedFilters)
    setFilters(initialState)
    if (!diff) { setLoadResultWithFilters(prevState => !prevState) }
  }

  const fetchOrganizations = async (): Promise<OrgFilterResults> => {
    setLoading(true)

    const addCountstoParams = (params: any, missingValue: MissingValueFilter): void => {
      params[missingValue.query] = missingValue.value
    }

    try {
      const params: any = {
        id: filters.id ?? filters.id,
        uniqueid: filters.uniqueId !== '' ? filters.uniqueId : undefined,
        name: filters.name !== '' ? filters.name : undefined,
        parent_orgs: filters.parent_orgs.length > 0 ? filters.parent_orgs.map((item) => item.id) : undefined,
        country: filters.country != null ? filters.country : undefined,
        type: filters.type !== 0 ? filters.type : undefined,
        page: filters.page !== 0 ? filters.page : undefined
      }
      if (filters.missing_value != null) { addCountstoParams(params, filters.missing_value) }
      params.order_by = filters.order_field
      params.order_direction = filters.order_direction
      return await orgListFilter(params)
    } catch (error) {
      console.error(error)
      return { data: [], page_number: 1, page_size: 1, total_pages: 1, total_items: 0 }
    }
  }

  const updateTableOrgsWithFilter = async (): Promise<void> => {
    fetchOrganizations().then(response => {
      setOrganizations(response.data)
      setPageCount(response.total_pages)
      setTotalCount(response.total_items)
      setLastQueriedFilters(filters)
      if (filters.page !== response.page_number) { updateFilters({ page: response.page_number }) }
    }).catch(reason => {
      console.log(reason)
      setOrganizations([])
      setPageCount(0)
    }).finally(() => { setLoading(false) })
  }

  useEffect(() => {
    const updateTablePage = async (): Promise<void> => {
      await updateTableOrgsWithFilter()
    }
    void updateTablePage()
  }, [loadResultWithFilters])

  function updateOrdering (field: string, direction: string): void {
    updateFilters({ order_field: field, order_direction: direction })
    setLoadResultWithFilters(prevState => !prevState)
  }

  return (
        <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Box width='95%' sx={{ pb: 1 }}>
                    <OrganizationsHeader/>
                    <OrganizationsFilters doFilter={updateTableOrgsWithFilter} filters={filters} updateFilters={updateFilters} cleanFilters={cleanFilter}/>
                    <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                        <Fab color="primary" aria-label="add" onClick={() => { navigate('/addOrganization') }}>
                            <AddIcon/>
                        </Fab>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {loading && <CircularProgress sx={{ alignSelf: 'center', mb: 3 }}/>}
                    {!loading && <OrganizationsTable organizations={organizations} orderField={filters.order_field} orderDirection={filters.order_direction} updateOrdering={updateOrdering}/>}
                    </Box>
                </Box>
                <Grid container justifyContent="space-between">
                  <Grid item xs/>
                  <Grid item>
                    <Pagination page={filters.page} count={pageCount} onChange={(event, page) => {
                      updateFilters({ page })
                      setLoadResultWithFilters(prevState => !prevState)
                    }}/>
                  </Grid>
                  <Grid item xs display="flex" justifyContent="flex-end">
                    <Typography variant="body1" sx={{ mr: 2 }}>
                      Page:<strong>{organizations.length}</strong> Total:<strong>{totalCount}</strong>
                    </Typography>
                  </Grid>
                </Grid>
            </Box>
        </Box>
  )
}
